import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  useColorModeValue,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { CompanyBrand } from '../models/companyBrand';
import useCompanyBrand from '../hooks/useCompanyBrand';
import { isDarkColor } from '../theme/brandTheme';
import {
  ColorField, FontField, isValidHex, isValidOptionalHex, LogoField,
} from './forms/BrandFields';

const STEPS = ['Welcome', 'Company name', 'Logo', 'Colors', 'Font', 'Preview', 'Getting around'] as const;

interface BrandWizardProps {
  isOpen: boolean;
  onClose: () => void;
  brand: CompanyBrand;
  /** first-login mode: can't be dismissed by accident, skip persists the flag */
  onboarding?: boolean;
}

/**
 * The guided brand process (item 35): a step-by-step walk through the company
 * brand package. Launched from Settings, and automatically on first login
 * while onboarding is incomplete (item 40).
 */
const BrandWizard: React.FC<BrandWizardProps> = ({
  isOpen, onClose, brand, onboarding,
}) => {
  const toast = useToast();
  const chainBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const { tryUpdate, isSaving } = useCompanyBrand();
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<CompanyBrand>(brand);

  useEffect(() => {
    if (isOpen) {
      setDraft(brand);
      setStep(0);
    }
    // re-seed the draft each time the wizard opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const set = (patch: Partial<CompanyBrand>) => setDraft((d) => ({ ...d, ...patch }));

  const colorsValid = isValidHex(draft.primaryColor)
    && isValidHex(draft.accentColor)
    && isValidOptionalHex(draft.successColor)
    && isValidOptionalHex(draft.warningColor)
    && isValidOptionalHex(draft.errorColor);

  const canAdvance = step !== 3 || colorsValid;

  const finish = async (values: CompanyBrand) => {
    try {
      await tryUpdate({ ...values, onboardingCompleted: true });
      toast({ status: 'success', title: 'Your brand is saved.' });
      onClose();
    } catch {
      toast({ status: 'error', title: 'Your brand was NOT saved.' });
    }
  };

  // onboarding skip still marks onboarding complete so the wizard stops re-appearing
  const skip = () => (onboarding ? finish(brand) : onClose());

  return (
    <Modal
      isOpen={isOpen}
      onClose={skip}
      size="xl"
      closeOnOverlayClick={false}
      closeOnEsc={!onboarding}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          GUIDED BRAND SETUP
          <Progress
            value={((step + 1) / STEPS.length) * 100}
            size="xs"
            colorScheme="brandGreen"
            mt={2}
          />
          <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={1}>
            {`Step ${step + 1} of ${STEPS.length} — ${STEPS[step]}`}
          </Text>
        </ModalHeader>
        <ModalBody minH="260px">
          {step === 0
          && (
            <Stack spacing={3}>
              <Heading size="md">Welcome to Basix Books!</Heading>
              <Text>
                Let&apos;s make the app yours. This quick setup walks you through your
                company&apos;s brand — your name, logo, colors and font. Everything you
                pick here restyles the whole app and can be changed anytime in Settings.
              </Text>
            </Stack>
          )}
          {step === 1
          && (
            <FormControl>
              <FormLabel>What&apos;s your company called?</FormLabel>
              <Input
                value={draft.companyName}
                maxLength={100}
                placeholder="Acme Builders"
                onChange={(e) => set({ companyName: e.target.value })}
              />
            </FormControl>
          )}
          {step === 2
          && (
            <Stack spacing={3}>
              <Text fontSize="sm">
                Upload your logo (optional — you can add it later in Settings).
              </Text>
              <LogoField value={draft.logoDataUrl} onChange={(v) => set({ logoDataUrl: v })} />
            </Stack>
          )}
          {step === 3
          && (
            <Stack spacing={4}>
              <HStack spacing={8}>
                <ColorField
                  label="Primary color"
                  value={draft.primaryColor}
                  onChange={(v) => set({ primaryColor: v ?? '' })}
                />
                <ColorField
                  label="Accent color"
                  value={draft.accentColor}
                  onChange={(v) => set({ accentColor: v ?? '' })}
                />
              </HStack>
              <Text fontSize="xs" color="gray.500">
                Primary is your main brand color (header, chrome); accent drives buttons
                and highlights. The optional colors below override the standard
                success/warning/error tones.
              </Text>
              <HStack spacing={8}>
                <ColorField label="Success" value={draft.successColor} onChange={(v) => set({ successColor: v })} optional />
                <ColorField label="Warning" value={draft.warningColor} onChange={(v) => set({ warningColor: v })} optional />
                <ColorField label="Error" value={draft.errorColor} onChange={(v) => set({ errorColor: v })} optional />
              </HStack>
            </Stack>
          )}
          {step === 4
          && <FontField value={draft.fontFamily} onChange={(v) => set({ fontFamily: v })} />}
          {step === 5
          && (
            <Stack spacing={4} style={{ fontFamily: `'${draft.fontFamily}', sans-serif` }}>
              <Flex
                align="center"
                gridGap={4}
                bg={draft.primaryColor}
                p={4}
                borderRadius="md"
              >
                {draft.logoDataUrl
                && (
                  <Image
                    src={draft.logoDataUrl}
                    maxH="40px"
                    objectFit="contain"
                    filter={isDarkColor(draft.primaryColor) ? 'brightness(0) invert(1)' : 'brightness(0)'}
                  />
                )}
                <Heading size="md" color={isDarkColor(draft.primaryColor) ? 'white' : 'gray.900'}>
                  {draft.companyName || 'Your Company'}
                </Heading>
              </Flex>
              <HStack>
                <Button bg={draft.accentColor} color="white" _hover={{ opacity: 0.8 }}>
                  ACTION BUTTON
                </Button>
                <Text>
                  Body text in
                  {' '}
                  {draft.fontFamily}
                  . This is how the app will look.
                </Text>
              </HStack>
              <Text fontSize="sm" color="gray.500">
                Happy with it? One more step — a quick tour of the app.
              </Text>
            </Stack>
          )}
          {step === 6
          && (
            <Stack spacing={4}>
              <Heading size="sm">Getting around Basix Books</Heading>
              <Text fontSize="sm">
                Everything is organized around your
                {' '}
                <b>Projects</b>
                . The money coming in flows through documents, in order:
              </Text>
              <HStack
                justify="center"
                p={3}
                bg={chainBg}
                borderRadius="md"
                fontSize="sm"
                fontWeight="bold"
                flexWrap="wrap"
              >
                <Text>PROJECT</Text>
                <Text color="gray.500">→</Text>
                <Text>PROPOSAL</Text>
                <Text color="gray.500">→</Text>
                <Text>WORK ORDER</Text>
                <Text color="gray.500">→</Text>
                <Text>INVOICE</Text>
              </HStack>
              <Text fontSize="sm">
                The money going out is tracked with
                {' '}
                <b>Purchase Orders</b>
                {' '}
                that become
                {' '}
                <b>Bills</b>
                .
              </Text>
              <Stack fontSize="sm" spacing={2}>
                <Text>
                  • The
                  {' '}
                  <b>sidebar</b>
                  {' '}
                  (left) switches between BASIX SIX — your document lists — and
                  PROJECTS, where each project lives.
                </Text>
                <Text>
                  • The
                  {' '}
                  <b>menu under your avatar</b>
                  {' '}
                  (top right) holds Settings, Users and your Item List.
                </Text>
                <Text>
                  • Change this brand anytime in
                  {' '}
                  <b>Settings → GUIDED BRAND SETUP</b>
                  .
                </Text>
              </Stack>
              <Text fontSize="sm" color="gray.500">
                That&apos;s it — finish to apply your brand and get started.
              </Text>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter>
          <Flex w="100%" justify="space-between">
            <Button variant="ghost" onClick={skip} isDisabled={isSaving}>
              {onboarding ? 'SKIP FOR NOW' : 'CANCEL'}
            </Button>
            <HStack>
              {step > 0
              && (
                <Button variant="outline" onClick={() => setStep((s) => s - 1)}>
                  BACK
                </Button>
              )}
              {step < STEPS.length - 1
              && (
                <Button onClick={() => setStep((s) => s + 1)} isDisabled={!canAdvance}>
                  NEXT
                </Button>
              )}
              {step === STEPS.length - 1
              && (
                <Button
                  onClick={() => finish(draft)}
                  isLoading={isSaving}
                  isDisabled={!colorsValid}
                >
                  FINISH
                </Button>
              )}
            </HStack>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

BrandWizard.defaultProps = { onboarding: false };

export default BrandWizard;
